// apps/dashboard/src/components/chrome/SidebarNav.tsx
import { BetaTag, LogoMark } from "@/design-system";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useLocalTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  ChevronLeft,
  LayoutGrid,
  Moon,
  Server,
  Settings,
  Sun
} from "lucide-react";
import { type ReactNode } from "react";
import { FeedbackButton } from "./FeedbackButton";
import { OrgSwitcher } from "./OrgSwitcher";
import { UserMenu } from "./UserMenu";

type NavItemProps = {
  to?: string;
  href?: string;
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  external?: boolean;
  collapsed?: boolean;
};

// Sidebar nav row — per the design system's nav.html: 13px sans, 6px/10px
// padding, 14px icon at 0.85 opacity. Active row fills with bg-2.
const NAV_BASE =
  "relative flex items-center gap-2 px-2.5 py-1.5 rounded-2 text-small transition-colors";

// Active row marker — a 2px accent rail pinned to the sidebar's left edge.
const ACTIVE_RAIL =
  "before:absolute before:-left-2 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-accent";

function NavItem({
  to,
  href,
  label,
  icon,
  disabled,
  external,
  collapsed,
}: NavItemProps) {
  const hover = collapsed ? "" : "hover:bg-bg-2 hover:text-fg-1";
  const labelCls = cn(
    "flex-1 whitespace-nowrap overflow-hidden transition-[opacity,max-width] duration-200 ease-out",
    collapsed ? "opacity-0 max-w-0" : "opacity-100 max-w-[160px]",
  );
  const title = collapsed ? label : undefined;
  if (disabled) {
    return (
      <span
        className={cn(NAV_BASE, "text-fg-3 opacity-55 cursor-default")}
        aria-disabled="true"
        title={title}
      >
        <NavIcon>{icon}</NavIcon>
        <span className={labelCls}>{label}</span>
      </span>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className={cn(NAV_BASE, "text-fg-2", hover)}
        title={title}
      >
        <NavIcon>{icon}</NavIcon>
        <span className={labelCls}>{label}</span>
        {external && !collapsed ? <span aria-hidden>↗</span> : null}
      </a>
    );
  }
  if (!to) {
    throw new Error("NavItem needs either `to` or `href`.");
  }
  return (
    <Link
      to={to}
      className={cn(NAV_BASE, "text-fg-2", hover)}
      activeProps={{
        className: collapsed
          ? cn("text-fg-1", ACTIVE_RAIL)
          : cn("bg-bg-2 text-fg-1 font-medium", ACTIVE_RAIL),
      }}
      title={title}
    >
      <NavIcon>{icon}</NavIcon>
      <span className={labelCls}>{label}</span>
    </Link>
  );
}

interface NavIconProps {
  children: ReactNode;
}

function NavIcon({ children }: NavIconProps) {
  return (
    <span className="size-3.5 inline-flex items-center justify-center shrink-0 opacity-85">
      {children}
    </span>
  );
}

interface SidebarGroupProps {
  title: string;
  collapsed: boolean;
  children: ReactNode;
}

function SidebarGroup({
  title,
  collapsed,
  children,
}: SidebarGroupProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <div
        className={cn(
          "px-2.5 font-sans text-tiny font-semibold uppercase tracking-[0.08em] text-fg-3 overflow-hidden transition-[opacity,height] duration-200",
          collapsed ? "opacity-0 h-0" : "opacity-100 h-5 pt-1",
        )}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

type SidebarNavProps = {
  collapsed?: boolean;
  onToggle?: () => void;
};

export function SidebarNav({ collapsed = false, onToggle }: SidebarNavProps) {
  return (
    <aside className="bg-bg-1 border-r border-border-1 flex flex-col relative h-full">
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          className="absolute top-4 -right-3 z-10 size-6 flex items-center justify-center rounded-full bg-bg-2 border border-border-1 text-fg-2 hover:text-fg-1 hover:border-border-2 shadow-sm transition-colors"
        >
          <ChevronLeft
            size={14}
            className={cn(
              "transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
              collapsed && "rotate-180",
            )}
          />
        </button>
      ) : null}

      <SidebarHeader collapsed={collapsed} />

      <nav className="flex flex-col gap-4 px-2 py-3.5 flex-1">
        <div className="px-0.5">
          <OrgSwitcher collapsed={collapsed} />
        </div>

        <SidebarGroup title="Workspace" collapsed={collapsed}>
          <NavItem
            to="/dashboard"
            label="Dashboard"
            icon={<LayoutGrid size={14} strokeWidth={1.7} />}
            collapsed={collapsed}
          />
          <NavItem
            to="/targets"
            label="Servers"
            icon={<Server size={14} strokeWidth={1.7} />}
            collapsed={collapsed}
          />
        </SidebarGroup>

        <SettingsNavSection collapsed={collapsed} />

        <div className="flex-1" />

        <ThemeToggle />
      </nav>

      <SidebarFooter collapsed={collapsed} />
    </aside>
  );
}

type CapabilityKey =
  | "canManageBilling"
  | "canViewAuditLog"
  | null;

interface SettingsSubnavItem {
  to: string;
  label: string;
  // null = visible to everyone; otherwise the capability that must be true.
  requires: CapabilityKey;
}

const ALL_SETTINGS_SUBNAV: readonly SettingsSubnavItem[] = [
  { to: "/settings/organization", label: "Organization", requires: null },
  { to: "/settings/members", label: "Members", requires: null },
  { to: "/settings/usage", label: "Usage", requires: null },
  { to: "/settings/billing", label: "Billing", requires: "canManageBilling" },
  { to: "/settings/audit", label: "Audit log", requires: "canViewAuditLog" },
] as const;

// Every org member sees the Account group; per-item visibility is gated
// by Clerk permissions via useCapabilities.
interface SettingsNavSectionProps {
  collapsed: boolean;
}

function SettingsNavSection({ collapsed }: SettingsNavSectionProps) {
  const capabilities = useCapabilities();
  if (!capabilities.loaded) return null;
  return (
    <SidebarGroup title="Account" collapsed={collapsed}>
      <SettingsNav collapsed={collapsed} capabilities={capabilities} />
    </SidebarGroup>
  );
}

// "Settings" is a parent row: when the active route is under /settings it
// expands an indented sub-nav tied to the parent by a hairline left rail.
interface SettingsNavProps {
  collapsed: boolean;
  capabilities: ReturnType<typeof useCapabilities>;
}

function SettingsNav({
  collapsed,
  capabilities,
}: SettingsNavProps) {
  const onSettings = useRouterState({
    select: (s) => s.location.pathname.startsWith("/settings"),
  });
  const subnav = ALL_SETTINGS_SUBNAV.filter((item) => {
    if (item.requires === null) return true;
    return capabilities[item.requires];
  });
  return (
    <div className="flex flex-col">
      <NavItem
        to="/settings"
        label="Settings"
        icon={<Settings size={14} strokeWidth={1.7} />}
        collapsed={collapsed}
      />
      {onSettings && !collapsed ? (
        <div className="ml-[22px] mt-0.5 mb-1 flex flex-col border-l border-border-1 pl-3">
          {subnav.map((item) => (
            <SettingsSubItem key={item.to} to={item.to} label={item.label} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface SettingsSubItemProps {
  to: string;
  label: string;
}

function SettingsSubItem({ to, label }: SettingsSubItemProps) {
  return (
    <Link
      to={to}
      className="flex items-center rounded-2 px-2.5 py-1 text-[12.5px] text-fg-3 transition-colors hover:bg-bg-2 hover:text-fg-1"
      activeProps={{
        className: cn(
          "bg-bg-2 font-medium text-fg-1",
          "before:content-[''] before:-ml-4 before:mr-2 before:size-1 before:rounded-full before:bg-accent",
        ),
      }}
    >
      {label}
    </Link>
  );
}

interface SidebarHeaderProps {
  collapsed: boolean;
}

function SidebarHeader({ collapsed }: SidebarHeaderProps) {
  return (
    <div className="border-b border-border-1 px-3 h-13 flex items-center shrink-0">
      <Link to="/dashboard" className="flex items-center gap-2 px-1 text-fg-1">
        <LogoMark size={20} />
        <span
          className={cn(
            "flex items-center gap-2 whitespace-nowrap overflow-hidden transition-[opacity,max-width] duration-200",
            collapsed ? "opacity-0 max-w-0" : "opacity-100 max-w-40",
          )}
        >
          <span className="text-[15px] font-semibold tracking-[-0.01em]">
            Gated
          </span>
          <BetaTag />
        </span>
      </Link>
    </div>
  );
}

interface SidebarFooterProps {
  collapsed: boolean;
}

function SidebarFooter({ collapsed }: SidebarFooterProps) {
  return (
    <div className="border-t border-border-1 p-3 flex flex-col gap-1">
      <FeedbackButton collapsed={collapsed} />
      <UserMenu collapsed={collapsed} />
    </div>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useLocalTheme();
  const isLight = theme === "light";

  return (
    <button
      type="button"
      onClick={() => setTheme(isLight ? "dark" : "light")}
      title={`Switch to ${isLight ? "dark" : "light"}`}
      aria-label={`Switch to ${isLight ? "dark" : "light"} theme`}
      className={cn(
        NAV_BASE,
        "justify-center text-fg-2 hover:bg-bg-2 hover:text-fg-1",
      )}
    >
      <NavIcon>
        {isLight ? (
          <Sun size={14} strokeWidth={1.7} />
        ) : (
          <Moon size={14} strokeWidth={1.7} />
        )}
      </NavIcon>
    </button>
  );
}
